import { HttpClient, PaginatedResponse, robotoHeaders } from "@/shared/http";

import {
  LayoutRecord,
  CreateLayoutRequest,
  UpdateLayoutRequest,
  isLayoutRecord,
  LayoutAccessibility,
} from "./LayoutRecord";

export class LayoutService {
  #httpClient: HttpClient;

  constructor(httpClient: HttpClient) {
    this.#httpClient = httpClient;
  }

  /**
   * @param conflictingLayouts The `conflictingLayouts` property from a `RobotoLayoutConflictException`
   *
   * @returns The user and shared layout records that caused the conflict
   * @throws Error if the conflicting layouts are not as expected
   */
  static parseRobotoLayoutConflictException(conflictingLayouts: unknown): {
    userLayout: LayoutRecord;
    sharedLayout: LayoutRecord;
  } {
    if (!Array.isArray(conflictingLayouts)) {
      throw new Error("Conflicting layouts is not an array");
    }

    if (!conflictingLayouts.every((layout) => isLayoutRecord(layout))) {
      throw new Error("Conflicting layouts is not an array of LayoutRecord");
    }

    const userLayout = conflictingLayouts.find(
      (layout) => layout.accessibility === LayoutAccessibility.User,
    );

    const sharedLayout = conflictingLayouts.find(
      (layout) => layout.accessibility === LayoutAccessibility.Organization,
    );

    if (!userLayout || !sharedLayout) {
      throw new Error("Conflicting layouts is missing a user or shared layout");
    }

    return { userLayout, sharedLayout };
  }

  public async createLayout(
    request: CreateLayoutRequest,
    orgId: string,
    options?: Partial<{
      abortSignal: AbortSignal;
    }>,
  ): Promise<LayoutRecord> {
    const requestUrl = this.#httpClient.constructUrl(`v1/layouts/create`);
    const response = await this.#httpClient.post(requestUrl, {
      signal: options?.abortSignal,
      headers: robotoHeaders({ orgId }),
      body: JSON.stringify(request),
    });
    return response.json<LayoutRecord>();
  }

  public async getLayoutById(
    layoutId: string,
    orgId: string,
    options?: Partial<{
      abortSignal: AbortSignal;
    }>,
  ): Promise<LayoutRecord> {
    const requestUrl = this.#httpClient.constructUrl(
      `v1/layouts/id/${layoutId}`,
    );
    const response = await this.#httpClient.get(requestUrl, {
      signal: options?.abortSignal,
      headers: robotoHeaders({ orgId }),
    });
    return response.json<LayoutRecord>();
  }

  public async getLayoutByName(
    layoutName: string,
    orgId: string,
    options?: Partial<{
      abortSignal: AbortSignal;
    }>,
  ): Promise<LayoutRecord> {
    const requestUrl = this.#httpClient.constructUrl(
      `v1/layouts/name/${encodeURIComponent(layoutName)}`,
    );
    const response = await this.#httpClient.get(requestUrl, {
      signal: options?.abortSignal,
      headers: robotoHeaders({ orgId }),
    });
    return response.json<LayoutRecord>();
  }

  public async getLayoutsForOrg(
    orgId: string,
    options?: Partial<{
      abortSignal: AbortSignal;
      pageToken: string;
      limit: number;
    }>,
  ): Promise<PaginatedResponse<LayoutRecord[]>> {
    const searchParams = new URLSearchParams();

    if (options?.pageToken !== undefined) {
      searchParams.append("page_token", options.pageToken);
    }

    const requestUrl = this.#httpClient.constructUrl(
      `v1/layouts`,
      searchParams,
    );

    const response = await this.#httpClient.get(requestUrl, {
      signal: options?.abortSignal,
      headers: robotoHeaders({ orgId }),
    });
    return response.json<PaginatedResponse<LayoutRecord[]>>();
  }

  public async updateLayout(
    layoutId: string,
    request: UpdateLayoutRequest,
    orgId: string,
    options?: Partial<{
      abortSignal: AbortSignal;
    }>,
  ): Promise<LayoutRecord> {
    const requestUrl = this.#httpClient.constructUrl(
      `v1/layouts/id/${layoutId}`,
    );
    const response = await this.#httpClient.put(requestUrl, {
      signal: options?.abortSignal,
      headers: robotoHeaders({ orgId }),
      body: JSON.stringify(request),
    });
    return response.json<LayoutRecord>();
  }

  public async deleteLayout(
    layoutId: string,
    orgId: string,
    options?: Partial<{
      abortSignal: AbortSignal;
    }>,
  ): Promise<void> {
    const requestUrl = this.#httpClient.constructUrl(
      `v1/layouts/id/${layoutId}`,
    );
    const response = await this.#httpClient.delete(requestUrl, {
      signal: options?.abortSignal,
      headers: robotoHeaders({ orgId }),
    });
    await response.throwIfError();
  }
}
